// 📄 src/pages/AutoReplyBuilder/components/AutoReplyCanvas.jsx
import React, {
  useCallback,
  useMemo,
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
  useEffect,
  createContext,
  useContext,
} from "react";
import ReactFlow, {
  Handle,
  MiniMap,
  Position,
  addEdge,
  MarkerType,
  useNodesState,
  useEdgesState,
  ReactFlowProvider,
} from "reactflow";
import "reactflow/dist/style.css";
import "./autoReplyReactFlowOverrides.css";

import { toast } from "react-toastify";
import { Eye, Minus, Plus, Trash2 } from "lucide-react";
import axiosClient from "../../../api/axiosClient";
import { NODE_ICONS } from "../../../utils/builderTokens";
import { listWhatsAppFlowSelector } from "../../../api/whatsAppFlows";
import { listAutoReplyCommercePolicyProfiles } from "../../../api/autoReplyCommercePolicyProfiles";
import AutoReplyNodeStart from "./AutoReplyNodeStart";
import AutoReplyNodeBlock from "./AutoReplyNodeBlock";
import AutoReplyNodeEditor from "./AutoReplyNodeEditor";
import AutoReplySmartEdge from "./edges/AutoReplySmartEdge";
import AutoReplyAlertModal from "./AutoReplyAlertModal";
import { validateAutoReplyConnection } from "../utils/graphValidation";
import { createBuilderGuid as createGuid } from "../utils/builderIds";
import { createConditionConfig } from "../utils/conditionValidation";
import { createDecisionTableConfig } from "../utils/decisionTableValidation";
import { createBrowseCatalogueConfig } from "../utils/browseCatalogueValidation";
import { createSearchCatalogueConfig } from "../utils/searchCatalogueValidation";
import { createPresentCatalogueResultsConfig } from "../utils/presentCatalogueResultsValidation";
import { createReviewAndSubmitConfig } from "../utils/reviewAndSubmitValidation";
import { createPrepareCommerceServiceConfig } from "../utils/prepareCommerceServiceValidation";
import { createWhatsAppFlowConfig } from "../utils/whatsAppFlowValidation";
import { createDynamicWhatsFlowConfig } from "../utils/dynamicWhatsFlowValidation";
import { createSchedulingBookingConfig } from "../utils/schedulingBookingValidation";
import { createConfirmBookingConfig } from "../utils/confirmBookingValidation";
import {
  createUrlCtaConfig,
  URL_CTA_AFTER_SEND,
} from "../utils/urlCtaValidation";

export const createAddFlowConfig = () => ({
  schemaVersion: 1,
  targetFlowId: null,
});

export const DEFAULT_HANDOVER_MESSAGE =
  "Agent handoff requested. A team member will follow up shortly.";

export const createHandoverConfig = () => ({
  ackText: DEFAULT_HANDOVER_MESSAGE,
});

export const normalizeAutoReplyFlowSummaries = payload => {
  const items = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.data)
    ? payload.data
    : Array.isArray(payload?.items)
    ? payload.items
    : Array.isArray(payload?.flows)
    ? payload.flows
    : [];

  return items
    .map(flow => ({
      id: String(flow?.id || "").trim(),
      name: String(flow?.name || flow?.flowName || "").trim(),
      isActive:
        typeof flow?.isActive === "boolean" ? flow.isActive : undefined,
      isDraft: typeof flow?.isDraft === "boolean" ? flow.isDraft : undefined,
      phoneNumberId: String(flow?.phoneNumberId || "").trim() || null,
    }))
    .filter(flow => flow.id && flow.name);
};

export const isEligibleAddFlowTarget = (
  flow,
  sourceFlowId,
  sourcePhoneNumberId
) => {
  if (!flow?.id) return false;
  if (
    sourceFlowId &&
    flow.id.toLowerCase() === String(sourceFlowId).trim().toLowerCase()
  ) {
    return false;
  }
  if (flow.isActive === false || flow.isDraft === true) return false;
  if (
    sourcePhoneNumberId &&
    flow.phoneNumberId &&
    flow.phoneNumberId !== sourcePhoneNumberId
  ) {
    return false;
  }
  return true;
};

export const AutoReplyAddFlowNode = ({ data, isConnectable }) => {
  const targetFlowId = String(data?.config?.targetFlowId || "").trim();
  const targetLabel = !targetFlowId
    ? "Select a flow"
    : data?.addFlowTargetUnavailable
    ? "Selected flow unavailable"
    : data?.addFlowTargetName || "Selected flow unavailable";
  const Icon = NODE_ICONS.add_flow;

  return (
    <div className="group relative min-h-[116px] w-[260px] max-w-[260px] rounded-lg border border-slate-200 bg-white p-0 text-[11px] font-normal text-slate-800 shadow-[0_2px_8px_rgba(0,0,0,0.04)] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)]">
      <Handle
        type="target"
        position={Position.Top}
        isConnectable={isConnectable}
        aria-label="Add Flow input"
        className="cursor-default"
        style={{
          width: 12,
          height: 12,
          top: -8,
          border: "2px solid #ffffff",
          borderRadius: "9999px",
          background: "#6366f1",
          boxShadow:
            "0 0 0 1px rgba(15, 23, 42, 0.08), 0 2px 4px rgba(15, 23, 42, 0.12)",
        }}
      />
      <div className="flex items-center justify-between rounded-t-lg border-b border-slate-100 bg-gradient-to-r from-indigo-50/70 to-white py-3 pl-4 pr-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <div className="flex h-6 w-6 items-center justify-center rounded-lg bg-white shadow-sm ring-1 ring-slate-200/60">
            <Icon
              className="h-3.5 w-3.5 shrink-0 text-indigo-600"
              strokeWidth={2.5}
              aria-hidden="true"
            />
          </div>
          <div className="truncate text-[12px] font-bold tracking-tight text-slate-800">
            Add Flow
          </div>
        </div>
        {data?.onDelete ? (
          <button
            type="button"
            onClick={event => {
              event.stopPropagation();
              data.onDelete(data.id);
            }}
            className="inline-flex h-5 w-5 items-center justify-center rounded-md text-slate-400 opacity-0 transition-all hover:bg-red-50 hover:text-red-500 group-hover:opacity-100"
            aria-label="Delete block"
            title="Delete block"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        ) : null}
      </div>
      <div className="p-3.5 pl-4">
        <p className="text-[9px] font-bold uppercase tracking-wide text-slate-400">
          Continue to
        </p>
        <p
          className={`mt-1 truncate text-[11px] font-normal leading-relaxed ${
            targetFlowId && data?.addFlowTargetUnavailable
              ? "text-amber-700"
              : "text-slate-700"
          }`}
          title={targetLabel}
        >
          {targetLabel}
        </p>
      </div>
    </div>
  );
};

const nodeTypes = {
  start: AutoReplyNodeStart,
  message: AutoReplyNodeBlock,
  input_capture: AutoReplyNodeBlock,
  media: AutoReplyNodeBlock,
  button_choice: AutoReplyNodeBlock,
  url_cta: AutoReplyNodeBlock,
  branch: AutoReplyNodeBlock,
  decision_table: AutoReplyNodeBlock,
  browse_catalogue: AutoReplyNodeBlock,
  search_catalogue: AutoReplyNodeBlock,
  present_catalogue_results: AutoReplyNodeBlock,
  prepare_commerce_service: AutoReplyNodeBlock,
  review_and_submit: AutoReplyNodeBlock,
  whatsapp_flow: AutoReplyNodeBlock,
  whatsapp_flow_dynamic_options: AutoReplyNodeBlock,
  schedule_booking: AutoReplyNodeBlock,
  confirm_booking: AutoReplyNodeBlock,
  // backward-compat aliases for older saved flows
  choice: AutoReplyNodeBlock,
  condition: AutoReplyNodeBlock,
  template: AutoReplyNodeBlock,
  wait: AutoReplyNodeBlock,
  tag: AutoReplyNodeBlock,
  set_lead_temperature: AutoReplyNodeBlock,
  cta_flow: AutoReplyNodeBlock, // 🆕 CTA Flow nodes use the same block renderer
  handover_agent: AutoReplyNodeBlock,
  add_flow: AutoReplyAddFlowNode,
};

const EDGE_COLOR = "#94a3b8"; // slate-400 — slightly darker for solid edges
const EDGE_STROKE_WIDTH = 2;
const EDGE_SELECTED_COLOR = "#dc2626";

const EdgeDeleteRequestContext = createContext(null);

const AutoReplyCanvasSmartEdge = props => {
  const onRequestDelete = useContext(EdgeDeleteRequestContext);
  return (
    <AutoReplySmartEdge
      {...props}
      onRequestDelete={onRequestDelete}
    />
  );
};

const edgeTypes = { smart: AutoReplyCanvasSmartEdge };

export const deleteAutoReplyNodeFromGraph = (nodesArg, edgesArg, nodeId) => ({
  nodes: (Array.isArray(nodesArg) ? nodesArg : []).filter(node => node.id !== nodeId),
  edges: (Array.isArray(edgesArg) ? edgesArg : []).filter(
    edge => edge.source !== nodeId && edge.target !== nodeId
  ),
});

export const serializeAutoReplyEdge = (edge, nodeMap) => ({
  id: edge.id,
  source: edge.source,
  target: edge.target,
  sourceNodeId: nodeMap[edge.source],
  targetNodeId: nodeMap[edge.target],
  sourceHandle: edge.sourceHandle ?? null,
  targetHandle: edge.targetHandle ?? null,
});

export const hydrateAutoReplyEdge = edge => ({
  id:
    edge.id ||
    `${edge.sourceNodeId}->${edge.targetNodeId}-${edge.sourceHandle || ""}`,
  source: edge.sourceNodeId,
  target: edge.targetNodeId,
  sourceHandle: edge.sourceHandle ?? null,
  targetHandle: edge.targetHandle ?? null,
});

const createStartNode = () => {
  const nodeId = createGuid();
  return {
    id: nodeId,
    type: "start",
    // Keep initial node near the top-left usable canvas area (right of the node rail).
    position: { x: 320, y: 80 },
    data: { id: nodeId, label: "start" },
  };
};

const getCanvasDraftKey = flowId =>
  `xbytechat:auto-reply:canvas-draft:${flowId || "new"}`;

const readCanvasDraft = flowId => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(getCanvasDraftKey(flowId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    if (!Array.isArray(parsed.nodes) || !Array.isArray(parsed.edges)) return null;
    return parsed;
  } catch {
    return null;
  }
};

const writeCanvasDraft = (flowId, payload) => {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(
      getCanvasDraftKey(flowId),
      JSON.stringify(payload)
    );
  } catch {
    // ignore storage failures
  }
};

const clearCanvasDraft = flowId => {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(getCanvasDraftKey(flowId));
  } catch {
    // ignore
  }
};

const getNodeType = node => {
  const raw = String(node?.type || "").trim();
  if (raw === "choice") return "button_choice";
  if (raw === "condition") return "branch";
  return raw;
};

const getFriendlyNodeName = node =>
  String(node?.data?.label || node?.type || "Step").trim();

const stripValidationStateFromNode = node => {
  if (!node?.data || !Object.prototype.hasOwnProperty.call(node.data, "validationError")) {
    return node;
  }

  const nextData = { ...node.data };
  delete nextData.validationError;
  return { ...node, data: nextData };
};

const stripValidationStateFromNodes = nodesArg =>
  Array.isArray(nodesArg) ? nodesArg.map(stripValidationStateFromNode) : [];

export const reconcileEdgesAfterNodeEdit = (previousNode, updatedNode, edgesArg) => {
  const edges = Array.isArray(edgesArg) ? edgesArg : [];
  const previousOptionCount = Array.isArray(previousNode?.data?.config?.options)
    ? previousNode.data.config.options.length
    : 0;
  const nextOptionCount = Array.isArray(updatedNode?.data?.config?.options)
    ? updatedNode.data.config.options.length
    : 0;
  const choiceOptionCountChanged =
    getNodeType(previousNode) === "button_choice" &&
    previousOptionCount !== nextOptionCount;
  const isChoiceNode = getNodeType(updatedNode) === "button_choice";
  const isConditionNode = getNodeType(updatedNode) === "branch";
  const isEndingUrlCtaNode =
    getNodeType(updatedNode) === "url_cta" &&
    updatedNode?.data?.config?.afterSend === URL_CTA_AFTER_SEND.END;
  const nextConditionHandles = new Set([
    ...(Array.isArray(updatedNode?.data?.config?.rules)
      ? updatedNode.data.config.rules.map(rule => String(rule?.pathId || "").trim()).filter(Boolean)
      : []),
    "default",
  ]);
  let removedOutOfRangeEdgeCount = 0;
  let removedConditionEdgeCount = 0;
  let removedUrlCtaEdgeCount = 0;
  let edgeLabelsChanged = false;

  const reconciledEdges = !isChoiceNode && !isConditionNode && !isEndingUrlCtaNode
    ? edges
    : edges.flatMap(edge => {
        if (edge.source !== updatedNode?.id) {
          return [edge];
        }

        if (isEndingUrlCtaNode) {
          removedUrlCtaEdgeCount += 1;
          return [];
        }

        const handle = String(edge?.sourceHandle || "").trim();
        if (isChoiceNode) {
          const optMatch = /^opt-(\d+)$/i.exec(handle);
          if (optMatch && Number(optMatch[1]) >= nextOptionCount) {
            removedOutOfRangeEdgeCount += 1;
            return [];
          }
        } else if (!nextConditionHandles.has(handle)) {
          removedConditionEdgeCount += 1;
          return [];
        }

        const nextLabel = getEdgeLabelFromSource(
          updatedNode,
          edge?.sourceHandle
        );
        if (nextLabel === edge.label) {
          return [edge];
        }

        edgeLabelsChanged = true;
        return [{ ...edge, label: nextLabel }];
      });

  return {
    choiceOptionCountChanged,
    addedOptionCount: Math.max(nextOptionCount - previousOptionCount, 0),
    removedOptionCount: Math.max(previousOptionCount - nextOptionCount, 0),
    removedOutOfRangeEdgeCount,
    removedConditionEdgeCount,
    removedUrlCtaEdgeCount,
    edgesChanged:
      removedOutOfRangeEdgeCount > 0 ||
      removedConditionEdgeCount > 0 ||
      removedUrlCtaEdgeCount > 0 ||
      edgeLabelsChanged,
    edges: reconciledEdges,
  };
};

const getEdgeLabelFromSource = (sourceNode, sourceHandle) => {
  const handle = String(sourceHandle || "").trim();
  const nodeType = getNodeType(sourceNode);

  if (!handle) {
    return getFriendlyNodeName(sourceNode);
  }

  if (nodeType === "button_choice") {
    const options = Array.isArray(sourceNode?.data?.config?.options)
      ? sourceNode.data.config.options
      : [];
    const optMatch = /^opt-(\d+)$/i.exec(handle);
    if (optMatch) {
      const idx = Number(optMatch[1]);
      const label = String(options[idx]?.label || "").trim();
      return label || `Button ${idx + 1}`;
    }
  }

  if (nodeType === "branch") {
    const rules = Array.isArray(sourceNode?.data?.config?.rules)
      ? sourceNode.data.config.rules
      : [];
    const matchedRule = rules.find(rule => String(rule?.pathId || "") === handle);
    if (matchedRule) return String(matchedRule.label || handle);
    const pathMap =
      sourceNode?.data?.config?.pathMap &&
      typeof sourceNode.data.config.pathMap === "object" &&
      !Array.isArray(sourceNode.data.config.pathMap)
        ? sourceNode.data.config.pathMap
        : {};
    const matchedKey = Object.entries(pathMap).find(
      ([, v]) => String(v || "").trim() === handle
    )?.[0];
    if (handle.toLowerCase() === "default") return "default";
    return matchedKey ? `${matchedKey}` : handle;
  }

  if (nodeType === "search_catalogue") {
    const labels = {
      no_results: "No Results",
      single_result: "Single Result",
      multiple_results: "Multiple Results",
    };
    return labels[handle] || handle;
  }

  if (nodeType === "browse_catalogue") {
    const labels = {
      selected: "Selected",
      no_categories: "No Categories",
    };
    return labels[handle] || handle;
  }

  if (nodeType === "present_catalogue_results") {
    const labels = {
      selected: "Selected",
      unavailable: "Unavailable",
    };
    return labels[handle] || handle;
  }

  if (nodeType === "prepare_commerce_service") {
    const labels = { prepared: "Prepared", processing_failed: "Processing failed" };
    return labels[handle] || handle;
  }

  if (nodeType === "review_and_submit") {
    const labels = {
      submitted: "Submitted",
      change_requested: "Change Requested",
      cancelled: "Cancelled",
    };
    return labels[handle] || handle;
  }

  if (nodeType === "template") {
    const buttonMatch = /^button-(\d+)$/i.exec(handle);
    const buttonIdx = buttonMatch ? Number(buttonMatch[1]) : null;
    const buttons = Array.isArray(sourceNode?.data?.config?.multiButtons)
      ? sourceNode.data.config.multiButtons
      : [];
    if (buttonIdx != null) {
      const buttonText = String(
        buttons[buttonIdx]?.buttonText || buttons[buttonIdx]?.text || ""
      ).trim();
      if (buttonText) return buttonText;
    }
  }

  return handle;
};

export const normalizeAutoReplyCanvasEdge = (edge, sourceNode) => ({
  ...edge,
  id:
    edge?.id ||
    `${edge?.source || "source"}->${edge?.target || "target"}-${edge?.sourceHandle || ""}`,
  type: "smart",
  label:
    String(edge?.label || "").trim() ||
    getEdgeLabelFromSource(sourceNode, edge?.sourceHandle),
  style: {
    ...(edge?.style || {}),
    stroke: EDGE_COLOR,
    strokeWidth: EDGE_STROKE_WIDTH,
  },
  markerEnd:
    edge?.markerEnd || { type: MarkerType.ArrowClosed, color: EDGE_COLOR },
});

export const normalizeAutoReplyConnection = (connection, sourceNode) => {
  if (getNodeType(sourceNode) !== "url_cta") return connection;

  return {
    ...connection,
    sourceHandle: String(connection?.sourceHandle || "").trim() || null,
    targetHandle: String(connection?.targetHandle || "").trim() || null,
  };
};

const AutoReplyCanvas = forwardRef((props, ref) => {
  const reactFlowWrapper = useRef(null);
  const draftHydratedRef = useRef(false);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);

  const [currentFlowId, setCurrentFlowId] = useState(null);
  const [autoReplyFlowOptions, setAutoReplyFlowOptions] = useState([]);

  const [nodes, setNodes, onNodesChange] = useNodesState([createStartNode()]);

  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedEdgeId, setSelectedEdgeId] = useState(null);

  useEffect(() => {
    if (!currentFlowId) return undefined;
    let cancelled = false;
    listWhatsAppFlowSelector()
      .then(items => {
        if (cancelled) return;
        const names = new Map(
          (Array.isArray(items) ? items : []).map(item => [
            `${item.definitionId}:${item.revisionId}`,
            item.name,
          ])
        );
        setNodes(current =>
          current.map(candidate => {
            if (getNodeType(candidate) !== "whatsapp_flow") return candidate;
            const config = candidate?.data?.config || {};
            const name = names.get(
              `${config.flowDefinitionId}:${config.flowRevisionId}`
            );
            if (!name || candidate?.data?.whatsAppFlowDisplayName === name)
              return candidate;
            return {
              ...candidate,
              data: { ...candidate.data, whatsAppFlowDisplayName: name },
            };
          })
        );
      })
      .catch(() => {
        // The stored node remains visible without exposing Form metadata.
      });
    return () => {
      cancelled = true;
    };
  }, [currentFlowId, setNodes]);

  useEffect(() => {
    if (!currentFlowId) return undefined;
    let cancelled = false;
    listAutoReplyCommercePolicyProfiles(true)
      .then(items => {
        if (cancelled) return;
        const profiles = new Map(
          (Array.isArray(items) ? items : []).map(item => [String(item.id), item])
        );
        setNodes(current => current.map(candidate => {
          if (getNodeType(candidate) !== "prepare_commerce_service") return candidate;
          const profileId = String(
            candidate?.data?.config?.commerceOrderPolicyProfileId || ""
          ).trim();
          const profile = profileId ? profiles.get(profileId) : null;
          const status = !profileId ? "default" : profile
            ? (profile.isActive ? "active" : "inactive")
            : "missing";
          const displayName = profile?.name || "";
          if (candidate?.data?.commerceOrderPolicyProfileStatus === status &&
              candidate?.data?.commerceOrderPolicyProfileDisplayName === displayName) {
            return candidate;
          }
          return {
            ...candidate,
            data: {
              ...candidate.data,
              commerceOrderPolicyProfileStatus: status,
              commerceOrderPolicyProfileDisplayName: displayName,
            },
          };
        }));
      })
      .catch(() => {
        // Backend activation remains authoritative if selector metadata is unavailable.
      });
    return () => { cancelled = true; };
  }, [currentFlowId, setNodes]);

  useEffect(() => {
    const hasAddFlowNode = nodes.some(
      node => String(node?.type || "").trim() === "add_flow"
    );
    if (!hasAddFlowNode) {
      setAutoReplyFlowOptions([]);
      return undefined;
    }

    let cancelled = false;
    Promise.resolve(axiosClient.get("autoreplyflows"))
      .then(response => {
        if (cancelled) return;
        const summaries = normalizeAutoReplyFlowSummaries(response?.data);
        const sourcePhoneNumberId = String(
          props?.sourcePhoneNumberId || ""
        ).trim();
        const projected = summaries.map(flow => ({
          ...flow,
          isEligible: isEligibleAddFlowTarget(
            flow,
            currentFlowId,
            sourcePhoneNumberId
          ),
        }));
        setAutoReplyFlowOptions(projected);
        setNodes(current => {
          let changed = false;
          const next = current.map(candidate => {
            if (String(candidate?.type || "").trim() !== "add_flow") {
              return candidate;
            }
            const targetFlowId = String(
              candidate?.data?.config?.targetFlowId || ""
            ).trim();
            const target = projected.find(
              flow => flow.id.toLowerCase() === targetFlowId.toLowerCase()
            );
            const targetName = target?.isEligible ? target.name : "";
            const unavailable = Boolean(targetFlowId) && !target?.isEligible;
            if (
              candidate?.data?.addFlowTargetName === targetName &&
              Boolean(candidate?.data?.addFlowTargetUnavailable) === unavailable
            ) {
              return candidate;
            }
            changed = true;
            return {
              ...candidate,
              data: {
                ...candidate.data,
                addFlowTargetName: targetName,
                addFlowTargetUnavailable: unavailable,
              },
            };
          });
          return changed ? next : current;
        });
      })
      .catch(() => {
        if (!cancelled) setAutoReplyFlowOptions([]);
      });

    return () => {
      cancelled = true;
    };
  }, [currentFlowId, nodes, props?.sourcePhoneNumberId, setNodes]);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [pendingDeleteEdgeId, setPendingDeleteEdgeId] = useState(null);
  const [showEdgeDeleteModal, setShowEdgeDeleteModal] = useState(false);
  const [showMiniMap, setShowMiniMap] = useState(false);

  // 🔄 tracking for “unsaved changes”
  const [lastSavedSnapshot, setLastSavedSnapshot] = useState(null);
  const [isDirty, setIsDirty] = useState(false);

  const addNodeSequenceRef = useRef(0);

  // easy access to advanced props from parent
  const {
    onSave,
    flowName = "",
    triggerKeywords = "",
    onFlowNameChange,
    onTriggerKeywordsChange,
    onDirtyChange,
    onContentStateChange,
  } = props;

  const updateFlowName = useCallback(
    value => {
      onFlowNameChange?.(value);
    },
    [onFlowNameChange]
  );

  const updateTriggerKeywords = useCallback(
    value => {
      onTriggerKeywordsChange?.(value);
    },
    [onTriggerKeywordsChange]
  );

  const applyDraftSnapshot = useCallback(
    draft => {
      const nextNodes =
        Array.isArray(draft?.nodes) && draft.nodes.length > 0
          ? stripValidationStateFromNodes(draft.nodes)
          : [createStartNode()];
      const sourceLookup = new Map(nextNodes.map(node => [node.id, node]));
      const nextEdges = Array.isArray(draft?.edges)
        ? draft.edges.map(edge =>
            normalizeAutoReplyCanvasEdge(
              edge,
              sourceLookup.get(edge?.source)
            )
          )
        : [];
      const selectedNodeId = String(draft?.selectedNodeId || "").trim();
      const nextSelectedNode =
        selectedNodeId && Array.isArray(nextNodes)
          ? nextNodes.find(n => n?.id === selectedNodeId) || null
          : null;

      setCurrentFlowId(draft?.flowId ?? null);
      updateFlowName(String(draft?.flowName || ""));
      updateTriggerKeywords(String(draft?.triggerKeywords || ""));
      setNodes(nextNodes);
      setEdges(nextEdges);
      setSelectedNode(nextSelectedNode);
      setSelectedEdgeId(null);
      setPendingDeleteId(null);
      setPendingDeleteEdgeId(null);
      setShowEdgeDeleteModal(false);
    },
    [setNodes, setEdges, updateFlowName, updateTriggerKeywords]
  );

  // --------------------------------------------------
  // snapshot helpers – used for dirty-state tracking
  // --------------------------------------------------
  const computeSnapshot = (
    nodesArg,
    edgesArg,
    flowNameArg,
    triggerKeywordsArg,
    flowIdArg
  ) => {
    const simpleNodes = (nodesArg || []).map(n => ({
      id: n.id,
      type: n.type,
      x: n.position?.x ?? 0,
      y: n.position?.y ?? 0,
      label: n.data?.label || "",
      config:
        n.data?.config && typeof n.data.config === "object"
          ? n.data.config
          : {},
    }));

    const simpleEdges = (edgesArg || []).map(e => ({
      id: e.id,
      source: e.source,
      target: e.target,
      sourceHandle: e.sourceHandle || null,
      targetHandle: e.targetHandle || null,
    }));

    return JSON.stringify({
      id: flowIdArg ?? null,
      name: flowNameArg || "",
      triggerKeyword: triggerKeywordsArg || "",
      nodes: simpleNodes,
      edges: simpleEdges,
    });
  };

  // recompute dirty flag whenever graph or meta changes
  useEffect(() => {
    if (!lastSavedSnapshot) {
      // no baseline yet (initial load / brand new flow)
      return;
    }

    const snap = computeSnapshot(
      nodes,
      edges,
      flowName,
      triggerKeywords,
      currentFlowId
    );
    setIsDirty(snap !== lastSavedSnapshot);
  }, [
    nodes,
    edges,
    flowName,
    triggerKeywords,
    currentFlowId,
    lastSavedSnapshot,
  ]);

  // Guard against browser refresh/navigation when there are unsaved edits.
  useEffect(() => {
    const handleBeforeUnload = event => {
      if (!isDirty) return;
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  useEffect(() => {
    const userNodeCount = nodes.filter(node => {
      const nodeType = String(node?.type || "").trim().toLowerCase();
      return nodeType !== "start";
    }).length;

    onContentStateChange?.({
      hasUserNodes: userNodeCount > 0,
      userNodeCount,
    });
  }, [nodes, onContentStateChange]);

  // Restore unsaved "new flow" draft on page mount/remount.
  useEffect(() => {
    if (draftHydratedRef.current) return;
    const draft = readCanvasDraft(null);
    if (draft) {
      applyDraftSnapshot(draft);
      setLastSavedSnapshot(
        computeSnapshot(
          draft.nodes,
          draft.edges,
          draft.flowName || "",
          draft.triggerKeywords || "",
          draft.flowId ?? null
        )
      );
      setIsDirty(true);
    }
    draftHydratedRef.current = true;
  }, [applyDraftSnapshot]);

  // Persist full canvas draft continuously so focus/tab switches cannot wipe progress.
  useEffect(() => {
    if (!draftHydratedRef.current) return;
    const draftNodes = stripValidationStateFromNodes(nodes);
    writeCanvasDraft(currentFlowId, {
      flowId: currentFlowId ?? null,
      flowName: flowName || "",
      triggerKeywords: triggerKeywords || "",
      nodes: draftNodes,
      edges: Array.isArray(edges) ? edges : [],
      selectedNodeId: selectedNode?.id || null,
      updatedAt: Date.now(),
    });
  }, [currentFlowId, flowName, triggerKeywords, nodes, edges, selectedNode]);

  // --------------------------------------------------
  // deletion helpers
  // --------------------------------------------------
  const handleDeleteNode = useCallback(
    nodeId => {
      setNodes(nds => deleteAutoReplyNodeFromGraph(nds, [], nodeId).nodes);
      setEdges(eds => deleteAutoReplyNodeFromGraph([], eds, nodeId).edges);
      setSelectedNode(null);
    },
    [setNodes, setEdges]
  );

  const confirmDeleteNode = useCallback(
    nodeId => {
      setPendingDeleteId(nodeId);
      setShowDeleteModal(true);
    },
    []
  );

  const closeNodeDeleteModal = useCallback(() => {
    setPendingDeleteId(null);
    setShowDeleteModal(false);
  }, []);

  const confirmDeleteNodeAction = useCallback(() => {
    if (!pendingDeleteId) return;
    handleDeleteNode(pendingDeleteId);
    setPendingDeleteId(null);
    setShowDeleteModal(false);
  }, [pendingDeleteId, handleDeleteNode]);

  const requestDeleteEdge = useCallback(edgeId => {
    if (!edgeId) return;
    setPendingDeleteEdgeId(edgeId);
    setShowEdgeDeleteModal(true);
  }, []);

  const closeEdgeDeleteModal = useCallback(() => {
    setPendingDeleteEdgeId(null);
    setShowEdgeDeleteModal(false);
  }, []);

  const confirmDeleteEdgeAction = useCallback(() => {
    if (!pendingDeleteEdgeId) return;
    setEdges(current => current.filter(edge => edge.id !== pendingDeleteEdgeId));
    setSelectedEdgeId(null);
    setPendingDeleteEdgeId(null);
    setShowEdgeDeleteModal(false);
  }, [pendingDeleteEdgeId, setEdges]);

  const getDefaultConfigByType = useCallback(type => {
    if (type === "input_capture") {
      return {
        question: "Please enter your response:",
        fieldKey: "custom.response",
        inputType: "text",
        invalidResponseMessage: "Please enter a valid response.",
        maximumAttempts: 3,
        minimum: null,
        maximum: null,
        minimumLength: null,
        maximumLength: null,
      };
    }
    if (type === "button_choice") {
      return {
        promptText: "Please choose an option:",
        options: [
          { label: "Button 1", value: "1" },
          { label: "Button 2", value: "2" },
        ],
      };
    }

    if (type === "branch") {
      return createConditionConfig();
    }

    if (type === "decision_table") {
      return createDecisionTableConfig();
    }

    if (type === "url_cta") {
      return createUrlCtaConfig();
    }

    if (type === "search_catalogue") {
      return createSearchCatalogueConfig();
    }

    if (type === "browse_catalogue") {
      return createBrowseCatalogueConfig();
    }

    if (type === "present_catalogue_results") {
      return createPresentCatalogueResultsConfig();
    }

    if (type === "prepare_commerce_service") {
      return createPrepareCommerceServiceConfig();
    }

    if (type === "review_and_submit") {
      return createReviewAndSubmitConfig();
    }

    if (type === "whatsapp_flow") {
      return createWhatsAppFlowConfig();
    }

    if (type === "whatsapp_flow_dynamic_options") {
      return createDynamicWhatsFlowConfig();
    }

    if (type === "schedule_booking") {
      return createSchedulingBookingConfig();
    }
    if (type === "confirm_booking") {
      return createConfirmBookingConfig();
    }

    if (type === "media") {
      return {
        mediaType: "image",
        mediaId: "",
        mediaUrl: "",
        caption: "",
        filename: "",
      };
    }

    if (type === "handover_agent") {
      return createHandoverConfig();
    }

    if (type === "set_lead_temperature") {
      return { leadTemperature: "Warm", allowTemperatureDowngrade: false };
    }
    if (type === "add_flow") {
      return createAddFlowConfig();
    }

    return {};
  }, []);

  const addNodeFromPalette = useCallback(
    (type, forcedPosition = null) => {
      if (!type) return;

      const insertIndex = addNodeSequenceRef.current++;
      let position = forcedPosition;

      if (!position) {
        if (reactFlowInstance && reactFlowWrapper.current) {
          const bounds = reactFlowWrapper.current.getBoundingClientRect();
          const center = reactFlowInstance.project({
            x: bounds.width / 2,
            y: bounds.height / 2,
          });
          position = {
            x: center.x + (insertIndex % 4) * 24,
            y: center.y + (insertIndex % 4) * 24,
          };
        } else {
          position = {
            x: 140 + (insertIndex % 4) * 24,
            y: 120 + (insertIndex % 4) * 24,
          };
        }
      }

      const newId = createGuid();
      const newNode = {
        id: newId,
        type,
        position,
        data: {
          id: newId,
          label: type,
          config: getDefaultConfigByType(type),
          onDelete: confirmDeleteNode,
        },
      };

      setNodes(nds => nds.concat(newNode));
    },
    [confirmDeleteNode, getDefaultConfigByType, reactFlowInstance, setNodes]
  );

  // --------------------------------------------------
  // ReactFlow callbacks
  // --------------------------------------------------
  const onConnect = useCallback(
    params => {
      const sourceNode = nodes.find(n => n.id === params.source);
      const normalizedParams = normalizeAutoReplyConnection(params, sourceNode);
      const validation = validateAutoReplyConnection(
        nodes,
        edges,
        normalizedParams
      );
      if (!validation.ok) {
        toast.error(validation.message);
        return;
      }

      const customId = `reactflow__edge-${normalizedParams.source}${
        normalizedParams.sourceHandle ? normalizedParams.sourceHandle : ""
      }-${normalizedParams.target}`;
      const edgeLabel = getEdgeLabelFromSource(
        sourceNode,
        normalizedParams.sourceHandle
      );

      setEdges(eds =>
        addEdge(
          normalizeAutoReplyCanvasEdge({
            ...normalizedParams,
            id: customId,
            label: edgeLabel,
          }, sourceNode),
          eds
        )
      );
    },
    [nodes, edges, setEdges]
  );

  const onEdgeClick = useCallback((event, edge) => {
    event.stopPropagation();
    setSelectedNode(null);
    setSelectedEdgeId(edge.id);
  }, []);

  const onPaneClick = useCallback(() => {
    setSelectedEdgeId(null);
  }, []);

  const handleEdgesChange = useCallback(
    changes => onEdgesChange(changes.filter(change => change.type !== "remove")),
    [onEdgesChange]
  );

  useEffect(() => {
    if (selectedEdgeId && !edges.some(edge => edge.id === selectedEdgeId)) {
      setSelectedEdgeId(null);
    }

  }, [edges, selectedEdgeId]);

  useEffect(() => {
    if (!selectedEdgeId) return undefined;

    const handleKeyDown = event => {
      if (event.key === "Escape") {
        setSelectedEdgeId(null);
        setPendingDeleteEdgeId(null);
        setShowEdgeDeleteModal(false);
        return;
      }

      if (event.key !== "Delete" && event.key !== "Backspace") return;
      const target = event.target;
      if (
        target instanceof HTMLElement &&
        (target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName))
      ) {
        return;
      }

      event.preventDefault();
      requestDeleteEdge(selectedEdgeId);
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [requestDeleteEdge, selectedEdgeId]);

  const renderedEdges = useMemo(
    () => edges.map(edge => ({
      ...edge,
      selected: edge.id === selectedEdgeId,
      markerEnd:
        edge.id === selectedEdgeId && edge.markerEnd && typeof edge.markerEnd === "object"
          ? { ...edge.markerEnd, color: EDGE_SELECTED_COLOR }
          : edge.markerEnd,
    })),
    [edges, selectedEdgeId]
  );

  const onDrop = useCallback(
    event => {
      event.preventDefault();
      const type = event.dataTransfer.getData("application/reactflow");
      if (!type || !reactFlowInstance || !reactFlowWrapper.current) return;

      const bounds = reactFlowWrapper.current.getBoundingClientRect();
      const position = reactFlowInstance.project({
        x: event.clientX - bounds.left,
        y: event.clientY - bounds.top,
      });

      addNodeFromPalette(type, position);
    },
    [addNodeFromPalette, reactFlowInstance]
  );

  const onDragOver = useCallback(event => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  const onNodeClick = useCallback((_, node) => {
    setSelectedEdgeId(null);
    setSelectedNode(node);
  }, []);

  const clearValidationMarkers = useCallback(() => {
    setNodes(prevNodes => stripValidationStateFromNodes(prevNodes));
    setSelectedNode(prevNode => stripValidationStateFromNode(prevNode));
  }, [setNodes]);

  const focusValidationNode = useCallback(
    (nodeId, message) => {
      if (!nodeId) return;

      const validationMessage =
        String(message || "Fix this node before saving.").trim() ||
        "Fix this node before saving.";
      let targetNode = null;

      setNodes(prevNodes =>
        prevNodes.map(node => {
          const isTarget = node?.id === nodeId || node?.data?.id === nodeId;

          if (isTarget) {
            const nextNode = {
              ...node,
              data: {
                ...(node?.data || {}),
                validationError: validationMessage,
              },
            };
            targetNode = nextNode;
            return nextNode;
          }

          return stripValidationStateFromNode(node);
        })
      );

      setSelectedNode(null);

      requestAnimationFrame(() => {
        const fallbackNode =
          targetNode ||
          nodes.find(node => node?.id === nodeId || node?.data?.id === nodeId) ||
          null;

        if (!fallbackNode || !reactFlowInstance) {
          return;
        }

        const currentZoom =
          typeof reactFlowInstance.getZoom === "function"
            ? reactFlowInstance.getZoom()
            : 0.9;

        reactFlowInstance.setCenter(
          (fallbackNode.position?.x || 0) + (fallbackNode.width || 260) / 2,
          (fallbackNode.position?.y || 0) + (fallbackNode.height || 140) / 2,
          {
            zoom: Math.max(currentZoom, 0.9),
            duration: 280,
          }
        );
      });
    },
    [nodes, reactFlowInstance, setNodes]
  );

  const handleNodeSave = updatedNode => {
    const previousNode = nodes.find(n => n.id === updatedNode.id);
    const reconciliation = reconcileEdgesAfterNodeEdit(
      previousNode,
      updatedNode,
      edges
    );

    setNodes(prev =>
      prev.map(n =>
        n.id === updatedNode.id ? stripValidationStateFromNode(updatedNode) : n
      )
    );

    if (reconciliation.edgesChanged) {
      setEdges(reconciliation.edges);
    }

    if (reconciliation.addedOptionCount > 0) {
      toast.warning(
        `${reconciliation.addedOptionCount} new choice option(s) need connections before saving. Existing connections were preserved.`
      );
    } else if (reconciliation.removedOutOfRangeEdgeCount > 0) {
      toast.warning(
        `${reconciliation.removedOutOfRangeEdgeCount} out-of-range choice connection(s) were removed. Existing valid connections were preserved.`
      );
    } else if (reconciliation.removedConditionEdgeCount > 0) {
      toast.warning(
        `${reconciliation.removedConditionEdgeCount} removed Condition rule connection(s) were discarded.`
      );
    } else if (reconciliation.removedUrlCtaEdgeCount > 0) {
      toast.warning(
        `${reconciliation.removedUrlCtaEdgeCount} URL Node outgoing connection(s) were removed because this path now ends after sending.`
      );
    }

    setSelectedNode(null);
  };

  // --------------------------------------------------
  // SAVE FLOW – builds DTO payload and delegates up
  // --------------------------------------------------
  const handleSaveFlow = async (options = {}) => {
    if (!reactFlowInstance) return;
    clearValidationMarkers();

    const flow = reactFlowInstance.toObject();
    const nodeMap = {};
    flow.nodes.forEach(node => {
      nodeMap[node.id] = node.data?.id || node.id;
    });

    const processedEdges =
      flow.edges?.map(edge => serializeAutoReplyEdge(edge, nodeMap)) || [];

    const processedNodes =
      flow.nodes?.map(node => {
        const id = node.data?.id || node.id;
        const nodeConfig =
          typeof node.data?.config === "object"
            ? JSON.parse(JSON.stringify(node.data.config))
            : {};

        return {
          id,
          type: node.type,
          position: { x: node.position.x, y: node.position.y },
          data: {
            label: node.data?.label || "",
            config: nodeConfig,
            followUp: node.data?.followUp || null,
            followUpEligible: node.data?.followUpEligible === true,
            followUpMaxHorizonSeconds: Number(node.data?.followUpMaxHorizonSeconds) || 0,
          },
        };
      }) || [];

      const payload = {
      id: currentFlowId,
      name: flowName.trim() || "Untitled Flow",
      triggerKeyword: triggerKeywords.trim() || "",
      nodes: processedNodes,
      edges: processedEdges,
    };

    if (typeof onSave === "function") {
      const saveResult = await onSave(payload, options);
      if (saveResult?.ok === false) {
        if (saveResult?.persistedDraft) {
          clearCanvasDraft(currentFlowId);
          const persistedSnapshot = computeSnapshot(
            processedNodes,
            processedEdges,
            payload.name,
            payload.triggerKeyword,
            saveResult?.flowId || currentFlowId
          );
          setCurrentFlowId(saveResult?.flowId || currentFlowId);
          setLastSavedSnapshot(persistedSnapshot);
          setIsDirty(false);
        }
        if (saveResult?.nodeId) {
          focusValidationNode(saveResult.nodeId, saveResult.message);
        }
        return saveResult;
      }

      clearCanvasDraft(currentFlowId);

      // update baseline snapshot on successful save
      const savedSnapshot = computeSnapshot(
        processedNodes,
        processedEdges,
        payload.name,
        payload.triggerKeyword,
        currentFlowId
      );
      setLastSavedSnapshot(savedSnapshot);
      setIsDirty(false);
      return saveResult;
    }

    toast.error("❌ Save function is not connected.");
  };

  // --------------------------------------------------
  // expose helpers to parent via ref
  // --------------------------------------------------
  useImperativeHandle(ref, () => ({
    loadFlow: (flowData, options = {}) => {
      try {
        setSelectedNode(null);
        setSelectedEdgeId(null);
        setPendingDeleteId(null);
        setPendingDeleteEdgeId(null);
        setShowEdgeDeleteModal(false);

        const flowId = flowData?.id ?? null;
        const draft = options?.ignoreDraft === true ? null : readCanvasDraft(flowId);

        setCurrentFlowId(flowId);
        updateFlowName(draft?.flowName || flowData?.name || "");
        updateTriggerKeywords(
          draft?.triggerKeywords || flowData?.triggerKeyword || flowData?.keyword || ""
        );

        const loadedNodes =
          flowData?.nodes?.map(node => {
            let config = {};
            if (typeof node.configJson === "string") {
              try {
                config = JSON.parse(node.configJson || "{}") || {};
              } catch {
                config = {};
              }
            } else if (node.configJson && typeof node.configJson === "object") {
              config = node.configJson;
            }

            const rawNodeType = node.nodeType;
            const normalizedNodeType =
              rawNodeType === "choice"
                ? "button_choice"
                : rawNodeType === "condition"
                ? "branch"
                : rawNodeType;

            return {
              id: node.id,
              type: normalizedNodeType,
              position: { x: node.positionX ?? 0, y: node.positionY ?? 0 },
              data: {
                id: node.id,
                label: node.label || node.nodeName || normalizedNodeType,
                config,
                followUp: node.followUp || null,
                followUpEligible: node.followUpEligible === true,
                followUpMaxHorizonSeconds: Number(flowData?.followUpMaxHorizonSeconds) || 0,
                legacyNodeType: rawNodeType === "condition" ? "condition" : null,
              },
            };
          }) || [];

        const nodeIds = new Set(loadedNodes.map(n => n.id));
        const loadedNodeById = new Map(loadedNodes.map(n => [n.id, n]));
        const hasBackendEdges =
          Array.isArray(flowData?.edges) && flowData.edges.length > 0;

        let loadedEdges = [];

        if (hasBackendEdges) {
          loadedEdges = flowData.edges
            .filter(
              e =>
                e?.sourceNodeId &&
                e?.targetNodeId &&
                nodeIds.has(e.sourceNodeId) &&
                nodeIds.has(e.targetNodeId)
            )
            .map(e =>
              normalizeAutoReplyCanvasEdge(
                hydrateAutoReplyEdge(e),
                loadedNodeById.get(e.sourceNodeId)
              )
            );
        } else {
          loadedNodes.forEach(node => {
            const outgoing = node.data?.config?.outgoing || [];
            outgoing.forEach((edgeCfg, idx) => {
              if (!edgeCfg?.targetId || !nodeIds.has(edgeCfg.targetId)) return;

              loadedEdges.push(normalizeAutoReplyCanvasEdge({
                id: `${node.id}->${edgeCfg.targetId}-${idx}`,
                source: node.id,
                target: edgeCfg.targetId,
                sourceHandle: edgeCfg.handle || null,
                label: getEdgeLabelFromSource(node, edgeCfg.handle),
              }, node));
            });
          });
        }

        const nextNodes = loadedNodes.length > 0 ? loadedNodes : [createStartNode()];
        const draftNodes =
          Array.isArray(draft?.nodes) && draft.nodes.length > 0
            ? stripValidationStateFromNodes(draft.nodes)
            : null;
        const draftEdges = Array.isArray(draft?.edges) ? draft.edges : null;
        const nextRenderedNodes = draftNodes || nextNodes;
        const sourceLookup = new Map(nextRenderedNodes.map(n => [n.id, n]));
        const nextRenderedEdges = (draftEdges || loadedEdges).map(edge =>
          normalizeAutoReplyCanvasEdge(
            edge,
            sourceLookup.get(edge?.source)
          )
        );
        const selectedNodeId = String(draft?.selectedNodeId || "").trim();

        setNodes(nextRenderedNodes);
        setEdges(nextRenderedEdges);
        setSelectedNode(
          selectedNodeId
            ? nextRenderedNodes.find(n => n?.id === selectedNodeId) || null
            : null
        );

        // establish clean baseline for dirty tracking
        const baselineSnapshot = computeSnapshot(
          nextNodes,
          loadedEdges,
          flowData?.name || "",
          flowData?.triggerKeyword || flowData?.keyword || "",
          flowData?.id ?? null
        );
        setLastSavedSnapshot(baselineSnapshot);
        setIsDirty(false);
      } catch (err) {
        console.error("Failed to load flow into canvas", err);
        toast.error("âŒ Failed to load flow on canvas");

        const fallbackNodes = [createStartNode()];
        setNodes(fallbackNodes);
        setEdges([]);
        setLastSavedSnapshot(computeSnapshot(fallbackNodes, [], "", "", null));
        setIsDirty(false);
      }
    },
    handleSaveFlow,
    saveFlow: options => handleSaveFlow(options),
    saveDraftFlow: () => handleSaveFlow({ mode: "draft" }),
    saveAndActivateFlow: () => handleSaveFlow({ mode: "activate" }),
    saveActiveChanges: () => handleSaveFlow({ mode: "save_active" }),
    showValidationError: (nodeId, message) =>
      focusValidationNode(nodeId, message),
    hasUnsavedChanges: () => isDirty,
    addNodeFromSidebar: nodeType => addNodeFromPalette(nodeType),
    clearFlow: () => {
      clearCanvasDraft(currentFlowId);
      clearCanvasDraft(null);

      setCurrentFlowId(null);
      updateFlowName("");
      updateTriggerKeywords("");
      setSelectedNode(null);
      setSelectedEdgeId(null);
      setPendingDeleteId(null);
      setPendingDeleteEdgeId(null);
      setShowEdgeDeleteModal(false);

      const defaultNodes = [createStartNode()];

      setNodes(defaultNodes);
      setEdges([]);

      const baselineSnapshot = computeSnapshot(defaultNodes, [], "", "", null);
      setLastSavedSnapshot(baselineSnapshot);
      setIsDirty(false);
    },
  }));

  // --------------------------------------------------
  // RENDER
  // --------------------------------------------------
  return (
    <ReactFlowProvider>
      <EdgeDeleteRequestContext.Provider value={requestDeleteEdge}>
      <div className="flex h-full w-full min-w-0 flex-col bg-[#f5f6f7]">
        {/* Canvas Area Container */}
        <div
          ref={reactFlowWrapper}
          className="relative flex flex-1 w-full flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)]"
          onDrop={onDrop}
          onDragOver={onDragOver}
        >
          <div className="relative flex-1">
          <ReactFlow
          nodes={nodes.map(n => ({
            ...n,
            data: {
              ...n.data,
              onDelete: confirmDeleteNode,
              ...(n.type === "start" ? { triggerKeywords } : {}),
              ...(n.type === "search_catalogue"
                ? {
                    browseSourceLabel:
                      nodes.find(
                        candidate =>
                          candidate.type === "browse_catalogue" &&
                          String(candidate.id).toLowerCase() ===
                            String(
                              n.data?.config?.categorySourceNodeId || ""
                            ).toLowerCase()
                      )?.data?.label || "",
                  }
                : {}),
              ...(n.type === "present_catalogue_results"
                ? {
                    searchNodeLabel:
                      nodes.find(
                        candidate =>
                          candidate.type === "search_catalogue" &&
                          String(candidate.id).toLowerCase() ===
                            String(n.data?.config?.searchNodeId || "").toLowerCase()
                      )?.data?.label || "",
                  }
                : {}),
            },
          }))}
          edges={renderedEdges}
          onNodesChange={onNodesChange}
          onEdgesChange={handleEdgesChange}
          onConnect={onConnect}
          onEdgeClick={onEdgeClick}
          onPaneClick={onPaneClick}
          onNodeClick={onNodeClick}
          deleteKeyCode={null}
          onInit={setReactFlowInstance}
          fitView={nodes.length > 1}
          fitViewOptions={{ padding: 0.5, maxZoom: 0.85 }}
          defaultViewport={{ x: 0, y: 0, zoom: 0.9 }}
          minZoom={0.2}
          maxZoom={2}
          snapToGrid
          snapGrid={[16, 16]}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          defaultEdgeOptions={{
            type: "smart",
            style: {
              stroke: "#94a3b8", // Slate gray
              strokeWidth: 2,
              strokeDasharray: "5, 5",
              zIndex: 1000,
            },
            markerEnd: { type: MarkerType.ArrowClosed, color: "#94a3b8" },
            zIndex: 1000,
          }}
          connectionLineStyle={{
            stroke: "#94a3b8",
            strokeWidth: 2,
            strokeDasharray: "5, 5",
          }}
          className="bg-transparent auto-reply-reactflow"
          elevateEdgesOnSelect={true}
        >
          {showMiniMap && (
            <MiniMap
              pannable
              zoomable
              className="!m-5 !overflow-hidden !rounded-2xl !border !border-slate-200/80 !bg-white/90 !shadow-[0_8px_32px_rgba(0,0,0,0.06)] !backdrop-blur-md"
              nodeColor={node => {
                const t = node.type === "choice" ? "button_choice" : node.type === "condition" ? "branch" : node.type;
                const fills = {
                  start:         "#d1fae5", // emerald-100
                  message:       "#dbeafe", // blue-100
                  button_choice: "#f3e8ff", // purple-100
                  branch:        "#fef3c7", // amber-100
                  decision_table:"#fef3c7", // amber-100
                  template:      "#f1f5f9", // slate-100
                  wait:          "#cffafe", // cyan-100
                  tag:           "#d1fae5", // emerald-100
                  cta_flow:      "#e0e7ff", // indigo-100
                  handover_agent:"#e0e7ff", // indigo-100
                };
                return fills[t] || "#f1f5f9";
              }}
              nodeStrokeColor={node => {
                const t = node.type === "choice" ? "button_choice" : node.type === "condition" ? "branch" : node.type;
                const strokes = {
                  start:         "#6ee7b7", // emerald-300
                  message:       "#93c5fd", // blue-300
                  button_choice: "#d8b4fe", // purple-300
                  branch:        "#fcd34d", // amber-300
                  decision_table:"#fcd34d", // amber-300
                  template:      "#cbd5e1", // slate-300
                  wait:          "#67e8f9", // cyan-300
                  tag:           "#6ee7b7", // emerald-300
                  cta_flow:      "#a5b4fc", // indigo-300
                  handover_agent:"#a5b4fc", // indigo-300
                };
                return strokes[t] || "#cbd5e1";
              }}
              nodeStrokeWidth={2}
              maskColor="rgba(248, 250, 252, 0.65)"
            />
          )}

          {/* Custom Bottom-Right Controls */}
          <div className="absolute bottom-5 right-5 z-50 flex items-center gap-2">
            <button
              onClick={() => setShowMiniMap(prev => !prev)}
              className="flex h-[38px] w-[38px] items-center justify-center rounded-full bg-emerald-600 text-white shadow-lg transition-colors hover:bg-emerald-700"
              title={showMiniMap ? "Hide MiniMap" : "Show MiniMap"}
            >
              {showMiniMap ? <Minus size={18} strokeWidth={2.5} /> : <Eye size={18} strokeWidth={2.5} />}
            </button>

            <div className="flex h-[38px] items-center gap-1.5 rounded-full border border-slate-200/80 bg-white/95 px-2.5 py-1.5 shadow-lg backdrop-blur-md">
              <button
                onClick={() => reactFlowInstance?.fitView({ duration: 300, padding: 0.25 })}
                className="rounded-full px-3 py-1 text-[13px] font-semibold tracking-wide text-slate-700 transition-colors hover:bg-slate-100"
                title="Fit to screen"
              >
                Fit
              </button>
              <div className="h-[18px] w-px bg-slate-200 mx-0.5"></div>
              <button
                onClick={() => reactFlowInstance?.zoomIn({ duration: 300 })}
                className="flex h-[26px] w-[26px] items-center justify-center rounded-full text-slate-700 transition-colors hover:bg-slate-100"
                title="Zoom In"
              >
                <Plus size={16} strokeWidth={2.5} />
              </button>
              <button
                onClick={() => reactFlowInstance?.zoomOut({ duration: 300 })}
                className="flex h-[26px] w-[26px] items-center justify-center rounded-full text-slate-700 transition-colors hover:bg-slate-100"
                title="Zoom Out"
              >
                <Minus size={16} strokeWidth={2.5} />
              </button>
            </div>
          </div>
        </ReactFlow>
          </div>

        <AutoReplyNodeEditor
          node={selectedNode}
          flowNodes={nodes}
          flowEdges={edges}
          sourceFlowId={currentFlowId}
          sourcePhoneNumberId={props?.sourcePhoneNumberId || ""}
          autoReplyFlowOptions={autoReplyFlowOptions}
          onClose={() => setSelectedNode(null)}
          onSave={handleNodeSave}
        />

        <AutoReplyAlertModal
          open={showDeleteModal}
          onClose={closeNodeDeleteModal}
          onConfirm={confirmDeleteNodeAction}
          title="Delete node?"
          message="Are you sure you want to delete this node? Its connected paths will also be removed."
          intent="danger"
          confirmLabel="Delete"
          compact
        />
        <AutoReplyAlertModal
          open={showEdgeDeleteModal}
          onClose={closeEdgeDeleteModal}
          onConfirm={confirmDeleteEdgeAction}
          title="Delete connection?"
          message="Are you sure you want to delete this connection? The flow may become incomplete."
          intent="danger"
          confirmLabel="Delete"
          compact
        />
      </div>
      </div>
      </EdgeDeleteRequestContext.Provider>
    </ReactFlowProvider>
  );
});

export default AutoReplyCanvas;



